/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import React from 'react';
import classNames from 'classnames';
import _ from 'underscore';

import CardView from './views/CardView';
import WarningCardView from './views/WarningCardView';
import BlankCardView from './views/BlankCardView';

export default class CardViewFactory {

	constructor({ model } = {}) {
		if (!model) {
			throw new Error('Invalid model provided to factory');
		}

		this.model = model;
	}

	/**
	 * Make card view
	 *
	 * @param {Card} card - card instance
	 * @return {React.Component} manufactured card view
	 */
	makeCardView(card) {
		let thumbnails;
		if (card.isCompareEnabled) {
			thumbnails = this.makeCompareCardView(card) || [];
		} else {
			thumbnails = this.makeSingleCardView(card) || [];
		}

		if (thumbnails.length === 0) {
			thumbnails.push(
				<BlankCardView
					key={_.uniqueId('blank_')}
				/>
			);
		}

		const classes = classNames({
			cardThumbnails: true,
			cardThumbnails__compare: card.isCompareEnabled,
			cardThumbnails__single: !card.isCompareEnabled
		});

		return <div className={classes}>
			{thumbnails}
		</div>;
	}

	/**
	 * Make compare card view
	 *
	 * @param {Card} card - card instance
	 * @return {React.Component} manufactured compare card view
	 */
	makeCompareCardView(card) {
		return card.getVisualizations().map((visualization) => {
			if (visualization.widgetLayout) {
				const { widgetLayout } = visualization;
				const widgetThumbnail = this.model.thumbnails.getItemByWidgetId(widgetLayout.id);
				if (widgetThumbnail) {
					if (widgetThumbnail.hasError() || widgetThumbnail.hasWarning()) {
						return this.makeWarningView(widgetThumbnail, widgetLayout.id);
					} else {
						return this.makeSuccessCardView(widgetThumbnail, widgetLayout.id);
					}
				} else {
					return this.makeBlankCardView();
				}
			} else if (visualization.placeholder) {
				return this.makeBlankCardView();
			}
		});
	}

	/**
	 * Make single card view
	 *
	 * @param {Card} card - card instance
	 * @return {React.Component} manufactured single card view
	 */
	makeSingleCardView(card) {
		let thumbnail;
		const visualizations = card.getVisualizations();
		if (visualizations.length === 0) {
			return;
		}

		const firstWidgetLayout = visualizations[0].widgetLayout;
		if (!firstWidgetLayout) {
			return;
		}

		const widgetThumbnail = this.model.thumbnails.getItemByWidgetId(firstWidgetLayout.id);
		if (!widgetThumbnail) {
			return;
		}

		if (widgetThumbnail.hasError() || widgetThumbnail.hasWarning()) {
			thumbnail = this.makeWarningView(widgetThumbnail);
		} else {
			thumbnail = this.makeSuccessCardView(widgetThumbnail, firstWidgetLayout.id);
		}

		if (thumbnail) {
			return [ thumbnail ];
		}
	}

	makeWarningView(thumbnail, id) {
		return (
			<WarningCardView
				key={_.uniqueId('warning_')}
				id={id}
				thumbnail={thumbnail}
			/>
		);
	}

	makeSuccessCardView(thumbnail, id) {
		return (
			<CardView
				key={id}
				id={id}
				thumbnail={thumbnail}
			/>
		);
	}

	makeBlankCardView() {
		return (
			<BlankCardView
				key={_.uniqueId('blank_')}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/js/components/cards/CardViewFactory.js